import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';
import {
  dateToStoredNumber,
  formatValueDisplay,
  isBlankValue,
  isMeasurableAchievedOnDay,
  isPercentUom,
  RAG_RATING_OPTIONS,
  SIC_INPUT_METHOD,
  SIC_VALUE_KIND,
  storedMinutesToTimeText,
  storedNumberToDate,
  timeTextToStoredMinutes,
  toNumberOrNull
} from 'helpers/sicHelper';
import { ragRatingColors, resultColors } from './sicTableStyles';

const transparentInputStyle = {
  width: '100%',
  minWidth: 0,
  border: 'none',
  background: 'transparent',
  color: 'inherit',
  fontWeight: 'inherit',
  textAlign: 'center',
  fontSize: '0.8rem',
  outline: 'none'
};

const yesNoSelectValue = value => {
  if (isBlankValue(value)) return '';
  return Number(value) === 1 ? '1' : '0';
};

const ragSelectValue = value => (isBlankValue(value) ? '' : String(Number(value)));

const selectValueToStoredValue = selectValue =>
  selectValue === '' ? null : Number(selectValue);

const NATIVE_DATE_INPUT_FORMAT = 'YYYY-MM-DD';

const toDraftText = (value, valueKind) => {
  if (isBlankValue(value)) return '';
  if (valueKind === SIC_VALUE_KIND.date) {
    return (
      storedNumberToDate(value)?.format(NATIVE_DATE_INPUT_FORMAT) ?? ''
    );
  }
  if (valueKind === SIC_VALUE_KIND.time) {
    return storedMinutesToTimeText(value);
  }
  return String(value);
};

const draftTextToStoredValue = (draftText, valueKind) => {
  if (draftText === '') return null;
  if (valueKind === SIC_VALUE_KIND.date) return dateToStoredNumber(draftText);
  if (valueKind === SIC_VALUE_KIND.time) {
    return timeTextToStoredMinutes(draftText);
  }
  return toNumberOrNull(draftText);
};

const INPUT_TYPE_BY_VALUE_KIND = {
  [SIC_VALUE_KIND.date]: 'date',
  [SIC_VALUE_KIND.time]: 'time'
};

export default function SicValueCell({
  measurable,
  dayIndex,
  isSaving,
  onCommit,
  styles,
  rowBackground
}) {
  const value = measurable.values[dayIndex];
  const { valueKind } = measurable;
  const [draftText, setDraftText] = useState(toDraftText(value, valueKind));

  useEffect(() => {
    setDraftText(toDraftText(value, valueKind));
  }, [value, valueKind]);

  const isEditable =
    measurable.inputMethod === SIC_INPUT_METHOD.manualEnter && !isSaving;
  const colors =
    valueKind === SIC_VALUE_KIND.rag
      ? ragRatingColors(styles, value)
      : resultColors(styles, isMeasurableAchievedOnDay(measurable, dayIndex));

  const commitDraft = () => {
    const draftStoredValue = draftTextToStoredValue(draftText, valueKind);
    if (draftStoredValue === toNumberOrNull(value)) {
      setDraftText(toDraftText(value, valueKind));
      return;
    }
    onCommit(draftStoredValue);
  };

  const renderEditor = () => {
    if (!isEditable) return formatValueDisplay(value, measurable);

    if (valueKind === SIC_VALUE_KIND.yesNo) {
      return (
        <select
          value={yesNoSelectValue(value)}
          onChange={e => onCommit(selectValueToStoredValue(e.target.value))}
          style={{ ...transparentInputStyle, cursor: 'pointer' }}
        >
          <option value="">-</option>
          <option value="1">Yes</option>
          <option value="0">No</option>
        </select>
      );
    }

    if (valueKind === SIC_VALUE_KIND.rag) {
      return (
        <select
          value={ragSelectValue(value)}
          onChange={e => onCommit(selectValueToStoredValue(e.target.value))}
          style={{ ...transparentInputStyle, cursor: 'pointer' }}
        >
          <option value="">-</option>
          {RAG_RATING_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    const valueInput = (
      <input
        type={INPUT_TYPE_BY_VALUE_KIND[valueKind] ?? 'number'}
        step={valueKind === SIC_VALUE_KIND.number ? 'any' : undefined}
        value={draftText}
        onChange={e => setDraftText(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
        style={transparentInputStyle}
      />
    );

    if (valueKind !== SIC_VALUE_KIND.number || !isPercentUom(measurable)) {
      return valueInput;
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {valueInput}
        <span style={{ marginLeft: '2px' }}>%</span>
      </div>
    );
  };

  return (
    <td
      style={{
        ...styles.bodyCell,
        backgroundColor: rowBackground,
        ...colors,
        fontWeight: Object.keys(colors).length ? 600 : 'normal',
        padding: '2px 4px',
        textAlign: 'center'
      }}
    >
      {isSaving ? <Spinner animation="border" size="sm" /> : renderEditor()}
    </td>
  );
}

SicValueCell.propTypes = {
  measurable: PropTypes.object.isRequired,
  dayIndex: PropTypes.number.isRequired,
  isSaving: PropTypes.bool,
  onCommit: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
  rowBackground: PropTypes.string
};
